import React, { useEffect, useState, Suspense } from 'react';
import { agentsAPI } from '../services/api';
import { Agent } from '../types';
import { Plus, Trash2, Code, Edit2, Eye, Copy, Check, Bot, Users } from 'lucide-react';
import AgentEditorModal from '../components/AgentEditorModal';
import EmbedCodeModal from '../components/EmbedCodeModal';
import ConfirmModal from '../components/ConfirmModal';
import PreviewModal from '../components/PreviewModal';
import AgentAccessModal from '../components/AgentAccessModal';
import { useToast } from '../contexts/ToastContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import usePermissions from '../hooks/usePermissions';
import { useBilling } from '../contexts/BillingContext';

// Plan-AutoInstallCommerceAgent §5d (2026-05-26) — per-agent Install /
// Manage-Widget button that flips the workspace's `shopify_agent_id`
// pointer via `workspaceAPI.reassignShopifyAgent`. Tree-shake-gated on
// `VITE_BILLING_PROVIDER`: the Stripe build never pulls the chunk.
const AgentShopifyAssignButton =
  import.meta.env.VITE_BILLING_PROVIDER === 'shopify'
    ? React.lazy(() => import('../components/agents/AgentShopifyAssignButton'))
    : null;

const Agents: React.FC = () => {
  const { showToast } = useToast();
  const { activeWorkspace } = useWorkspace();
  // Backend auto-stamps `workspaces.shopify_agent_id` on agent create (and a
  // few other flows). The billing payload caches that pointer, so we must
  // refresh it after create / publish or the per-agent
  // AgentShopifyAssignButton stays at "Install to Shopify Store" until a
  // hard reload.
  const { refetch: refetchBilling } = useBilling();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [selectedAgentName, setSelectedAgentName] = useState('');
  // Plan-ComponentEmbed — slug for the embed modal's component snippet
  // URLs, + the gate flag threaded straight from the SAME getEmbedCode
  // response (no extra fetch).
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [componentEmbedEnabled, setComponentEmbedEnabled] = useState(false);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState<{ id: string, name: string } | null>(null);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [agentToPublish, setAgentToPublish] = useState<{ id: string, name: string } | null>(null);

  const [previewAgent, setPreviewAgent] = useState<Agent | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [copiedEmailId, setCopiedEmailId] = useState<string | null>(null);
  const [accessAgent, setAccessAgent] = useState<Agent | null>(null);
  const [showAccessModal, setShowAccessModal] = useState(false);

  // Was inline: `wsIsOwner || currentUser?.is_owner || currentUser?.roles?.includes('admin')`.
  const { isAdminOrOwner } = usePermissions();

  const handleCopyEmail = (agentId: string, email: string) => {
    navigator.clipboard.writeText(email);
    setCopiedEmailId(agentId);
    setTimeout(() => setCopiedEmailId(null), 2000);
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await agentsAPI.list();
      setAgents(response.data);
    } catch (error) {
      console.error('Failed to fetch agents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (agentId: string, agentName: string) => {
    setAgentToDelete({ id: agentId, name: agentName });
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!agentToDelete) return;
    try {
      await agentsAPI.delete(agentToDelete.id);
      fetchAgents();
    } catch (error) {
      console.error('Failed to delete agent:', error);
    } finally {
      setAgentToDelete(null);
    }
  };

  const handleGetEmbedCode = async (agentId: string, agentName: string) => {
    try {
      const response = await agentsAPI.getEmbedCode(agentId);
      setEmbedCode(response.data.embed_code);
      setSelectedAgentName(agentName);
      setSelectedAgentId(agentId);
      setComponentEmbedEnabled(response.data.component_embed_enabled === true);
      setShowEmbedModal(true);

    } catch (error: any) {
      console.error('Failed to get embed code:', error);
      showToast(error.response?.data?.detail || 'Failed to get embed code', 'error');
    }
  };

  const handleTogglePublish = async (agentId: string, currentStatus: boolean, agentName: string) => {
    if (!currentStatus) {
      setAgentToPublish({ id: agentId, name: agentName });
      setShowPublishModal(true);
    } else {
      await executeTogglePublish(agentId, false);
    }
  };

  const executeTogglePublish = async (agentId: string, newStatus: boolean) => {
    try {
      await agentsAPI.update(agentId, { is_published: newStatus });
      fetchAgents();
      if (newStatus) {
        showToast('Agent is now live in production!', 'success');
        // Backend may have stamped `workspaces.shopify_agent_id` as part of the
        // publish flow — refresh billing so the per-agent Install / Manage
        // Widget label flips without requiring a hard reload. Fire after the
        // toast so a billing hiccup never blocks the success UX, and guard
        // so a refetch failure is logged but swallowed.
        try {
          await refetchBilling();
        } catch (e) {
          console.warn('refetchBilling after publish failed (non-fatal):', e);
        }
      } else {
        showToast('Agent unpublished.', 'info');
      }
    } catch (error: any) {
      console.error('Failed to toggle publish status:', error);
      showToast(error.response?.data?.detail || 'Failed to update agent status', 'error');
    }
  };

  const handlePublishConfirm = () => {
    if (agentToPublish) {
      executeTogglePublish(agentToPublish.id, true);
      setAgentToPublish(null);
    }
  };

  const handleOnboardingComplete = async () => {
    // Close modal + refresh agent list first so the success UX never blocks
    // on the billing refetch below.
    setShowCreateModal(false);
    setEditingAgent(null);
    fetchAgents();
    // Backend POST /api/agents auto-stamps `workspaces.shopify_agent_id` on
    // first-agent creation for Shopify-billed workspaces. Refresh billing so
    // the AgentShopifyAssignButton flips from "Install to Shopify Store" to
    // "Manage Widget" without requiring a hard reload. Guarded — a billing
    // fetch hiccup must never break the create UX.
    try {
      await refetchBilling();
    } catch (e) {
      console.warn('refetchBilling after onboarding complete failed (non-fatal):', e);
    }
  };

  const handleEditClick = (agent: Agent) => {
    setEditingAgent(agent);
    setShowCreateModal(true);
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;

  return (
    <div className="space-y-6">
      <AgentEditorModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingAgent(null);
        }}
        onComplete={handleOnboardingComplete}
        isEdit={!!editingAgent}
        initialConfig={editingAgent?.config}
        agentId={editingAgent?.agent_id}
        agentEmail={editingAgent?.email}
        s3Assets={editingAgent?.s3_assets}
        helpCenterSlug={editingAgent?.help_center_slug}
      />

      <EmbedCodeModal
        isOpen={showEmbedModal}
        onClose={() => setShowEmbedModal(false)}
        embedCode={embedCode}
        agentName={selectedAgentName}
        agentId={selectedAgentId}
        componentEmbedEnabled={componentEmbedEnabled}
      />

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Agent"
        message={`Are you sure you want to delete "${agentToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDangerous={true}
      />

      <ConfirmModal
        isOpen={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handlePublishConfirm}
        title="Publish Agent to Production"
        message={`Are you sure you want to publish "${agentToPublish?.name}"? This will make the agent live and accessible to your customers. Please ensure you have tested the agent thoroughly before proceeding.`}
        confirmText="Publish"
        cancelText="Cancel"
        isDangerous={false}
      />

      <PreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        agent={previewAgent}
      />

      {accessAgent && activeWorkspace && (
        <AgentAccessModal
          isOpen={showAccessModal}
          onClose={() => { setShowAccessModal(false); setAccessAgent(null); }}
          agent={accessAgent}
          workspaceId={activeWorkspace.id}
          // `.id` is `str(agent._id)` — the identifier `agent_ids` stores.
          // NOT `.agent_id`, which is the slug.
          allAgentIds={agents.map(a => a.id)}
        />
      )}

      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AI Agents</h1>
        <button
          data-tour-target="agent-create-button"
          onClick={() => {
            setEditingAgent(null);
            setShowCreateModal(true);
          }}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Create Agent</span>
        </button>
      </div>

      {agents.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-600 dark:text-gray-400 mb-4">No agents yet. Create your first AI agent!</p>
          <button
            data-tour-target="agent-create-button"
            onClick={() => {
              setEditingAgent(null);
              setShowCreateModal(true);
            }}
            className="btn-primary"
          >
            Create Agent
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {agents.map((agent) => (
            <div key={agent.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">{agent.config.agent_name}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{agent.config.company_name}</p>
                  {agent.config.industry && (
                    <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {agent.config.industry?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </span>
                  )}
                </div>
                <div className={`px-2 py-1 rounded text-xs font-medium ${agent.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'}`}>
                  {agent.is_active ? 'Active' : 'Inactive'}
                </div>
              </div>

              <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Published:</span>
                  <button
                    onClick={() => handleTogglePublish(agent.agent_id, agent.is_published, agent.config.agent_name)}
                    data-tour-target="agent-publish-toggle"
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${agent.is_published ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${agent.is_published ? 'translate-x-6' : 'translate-x-1'
                        }`}
                    />
                  </button>
                  <span className={`text-xs font-medium ${agent.is_published ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'}`}>
                    {agent.is_published ? 'Live' : 'Draft'}
                  </span>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Conversations:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{agent.total_conversations}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Messages:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{agent.total_messages}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Resolution Rate:</span>
                  <span className="font-medium text-gray-900 dark:text-white">{agent.auto_resolution_rate.toFixed(1)}%</span>
                </div>
                {agent.email && (
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-gray-600 dark:text-gray-400 flex-shrink-0">Email:</span>
                    <div className="flex items-center gap-1 ml-2 min-w-0">
                      <span
                        className="font-mono text-xs text-gray-900 dark:text-white truncate max-w-[130px]"
                        title={agent.email}
                      >
                        {agent.email}
                      </span>
                      <button
                        onClick={() => handleCopyEmail(agent.agent_id, agent.email!)}
                        className="flex-shrink-0 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title="Copy email address"
                      >
                        {copiedEmailId === agent.agent_id
                          ? <Check className="w-3 h-3 text-green-500" />
                          : <Copy className="w-3 h-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />}
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex justify-between text-sm items-center">
                  <span className="text-gray-600 dark:text-gray-400">Auto-Reply:</span>
                  {agent.config.auto_respond_emails ? (
                    <span className="text-[10px] font-medium text-purple-700 bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Bot className="w-3 h-3" /> Active
                    </span>
                  ) : (
                    <span className="text-[10px] font-medium text-gray-500 bg-gray-100 dark:bg-gray-800 dark:text-gray-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Bot className="w-3 h-3" /> Inactive
                    </span>
                  )}
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  data-tour-target="agent-embed-code"
                  onClick={() => handleGetEmbedCode(agent.agent_id, agent.config.agent_name)}
                  className="flex-1 btn-secondary flex items-center justify-center space-x-1"
                  title="Get embed code"
                >
                  <Code className="w-4 h-4" />
                  <span>Embed</span>
                </button>
                <button
                  onClick={() => {
                    setPreviewAgent(agent);
                    setShowPreviewModal(true);
                  }}
                  className="btn-secondary text-primary p-2"
                  title="Preview widget"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleEditClick(agent)}
                  className="btn-secondary text-blue-600 dark:text-blue-400 p-2"
                  title="Edit agent settings"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteClick(agent.agent_id, agent.config.agent_name)}
                  className="btn-secondary text-red-600 dark:text-red-400 p-2"
                  title="Delete agent"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Plan-AutoInstallCommerceAgent §5d — per-agent Install /
                  Manage-Widget button. Reassigns the workspace's
                  designated Shopify agent when clicked on a non-active
                  card; deep-links to /integrations for the active one. */}
              {AgentShopifyAssignButton && (
                <Suspense fallback={null}>
                  <AgentShopifyAssignButton
                    agentId={agent.id}
                    agentSlug={agent.agent_id}
                    agentName={agent.config.agent_name}
                    isPublished={agent.is_published}
                  />
                </Suspense>
              )}

              {isAdminOrOwner && activeWorkspace && (
                <button
                  onClick={() => { setAccessAgent(agent); setShowAccessModal(true); }}
                  className="w-full mt-2 btn-secondary flex items-center justify-center space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                  title="Manage team access for this agent"
                >
                  <Users className="w-4 h-4" />
                  <span>Team Access</span>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Agents;
